import { apiRequest } from '../utils/apiResponseHandler';
import { getApiUrl } from '../config/api';

const invoiceService = {
  // Invoice Management
  async getInvoices(filters = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });
    const url = queryParams.toString()
      ? `${getApiUrl('INVOICES')}?${queryParams.toString()}`
      : getApiUrl('INVOICES');
    // No client-side timeout: large list may take a long time to transfer
    return await apiRequest(url, { method: 'GET', timeout: 0 });
  },

  async getInvoiceById(invoiceId) {
    return await apiRequest(getApiUrl('INVOICE_BY_ID', invoiceId), { method: 'GET' });
  },

  async createInvoice(invoiceData) {
    return await apiRequest(getApiUrl('INVOICE_CREATE'), {
      method: 'POST',
      body: invoiceData
    });
  },

  async updateInvoice(invoiceId, invoiceData) {
    return await apiRequest(getApiUrl('INVOICE_UPDATE', invoiceId), {
      method: 'PUT',
      body: invoiceData
    });
  },

  async deleteInvoice(invoiceId) {
    return await apiRequest(getApiUrl('INVOICE_BY_ID', invoiceId), { method: 'DELETE' });
  },

  // Enhanced Invoice Creation
  async createInvoiceEnhanced(invoiceData) {
    return await apiRequest(getApiUrl('INVOICE_CREATE_ENHANCED'), {
      method: 'POST',
      body: invoiceData
    });
  },

  // Xero Integration
  async sendInvoiceToXero(invoiceId) {
    return await apiRequest(getApiUrl('INVOICE_SEND_TO_XERO', invoiceId), {
      method: 'POST'
    });
  },

  // Email Invoice
  async sendInvoiceEmail(invoiceId) {
    return await apiRequest(getApiUrl('INVOICE_SEND_EMAIL', invoiceId), {
      method: 'POST'
    });
  },

  // Public payment (tokenised, no auth)
  async getPublicInvoice(token) {
    return await apiRequest(getApiUrl('INVOICE_PUBLIC_PAY', token), { method: 'GET' });
  },

  async createCheckoutSession(token) {
    return await apiRequest(getApiUrl('INVOICE_CREATE_CHECKOUT', token), { method: 'POST' });
  },

  // PayPal (tokenised, no auth)
  async createPaypalOrder(token) {
    return await apiRequest(getApiUrl('INVOICE_CREATE_PAYPAL_ORDER', token), { method: 'POST' });
  },

  async capturePaypalOrder(token, orderId) {
    return await apiRequest(getApiUrl('INVOICE_CAPTURE_PAYPAL_ORDER', token), {
      method: 'POST',
      body: { order_id: orderId },
    });
  },

  async bulkSendInvoices(invoiceIds, options = {}) {
    return await apiRequest(getApiUrl('INVOICE_BULK_SEND'), {
      method: 'POST',
      body: {
        invoice_ids: invoiceIds,
        send_emails: options.send_emails || false,
        dry_run: options.dry_run || false
      }
    });
  },

  // Bulk Invoice Operations
  async bulkCreateInvoices(invoiceData) {
    return await apiRequest(getApiUrl('INVOICE_BULK_CREATE'), {
      method: 'POST',
      body: invoiceData
    });
  },

  async createAndSendNewMemberInvoice(invoiceData) {
    return await apiRequest(getApiUrl('INVOICE_CREATE_AND_SEND_NEW_MEMBER'), {
      method: 'POST',
      body: invoiceData
    });
  },

  async bulkAnnualRenewalInvoices(invoiceData) {
    return await apiRequest(getApiUrl('INVOICE_BULK_ANNUAL_RENEWAL'), {
      method: 'POST',
      body: invoiceData
    });
  },


  // Xero Integration Management
  async getXeroSyncStatus() {
    return await apiRequest(getApiUrl('XERO_SYNC_STATUS'), { method: 'GET' });
  },

  async testXeroConnection() {
    return await apiRequest(getApiUrl('XERO_TEST_CONNECTION'), { method: 'POST' });
  },

  async getXeroAccounts() {
    return await apiRequest(getApiUrl('XERO_ACCOUNTS'), { method: 'GET' });
  },


  async syncInvoicesToXero() {
    // Triggers background task - returns immediately
    return await apiRequest(getApiUrl('XERO_SYNC_INVOICES'), {
      method: 'POST'
    });
  },

  /** Sync invoice status from Xero (e.g. Paid in Xero -> paid on website). Runs in background. */
  async syncStatusFromXero() {
    return await apiRequest(getApiUrl('XERO_SYNC_STATUS_FROM_XERO'), {
      method: 'POST'
    });
  },

  // Bulk Invoice Operations Management
  async getBulkInvoiceOperations(filters = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });
    const url = queryParams.toString()
      ? `${getApiUrl('BULK_INVOICE_OPERATIONS')}?${queryParams.toString()}`
      : getApiUrl('BULK_INVOICE_OPERATIONS');
    return await apiRequest(url, { method: 'GET' });
  },

  async createBulkInvoiceOperation(operationData) {
    return await apiRequest(getApiUrl('BULK_INVOICE_OPERATIONS_CREATE'), {
      method: 'POST',
      body: operationData
    });
  },

  async previewBulkInvoiceOperation(operationData) {
    return await apiRequest(getApiUrl('BULK_INVOICE_OPERATIONS_PREVIEW'), {
      method: 'POST',
      body: operationData
    });
  },

  async startBulkInvoiceOperation(operationId) {
    return await apiRequest(getApiUrl('BULK_INVOICE_OPERATIONS_START', operationId), {
      method: 'POST'
    });
  },

  async getBulkInvoiceOperationStatus(operationId) {
    return await apiRequest(getApiUrl('BULK_INVOICE_OPERATIONS_STATUS', operationId), { method: 'GET' });
  },

  async getBulkInvoiceOperationSummary(operationId) {
    return await apiRequest(getApiUrl('BULK_INVOICE_OPERATIONS_SUMMARY', operationId), { method: 'GET' });
  },

  // Invoice Export
  async exportInvoices(filters = {}, format = 'json') {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });
    queryParams.append('format', format);

    const url = `${getApiUrl('INVOICE_EXPORT')}?${queryParams.toString()}`;
    return await apiRequest(url, { method: 'GET' });
  },



  // Get Members (for invoice creation)
  async getMembers(filters = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });
    const url = queryParams.toString()
      ? `${getApiUrl('MEMBERS')}?${queryParams.toString()}`
      : getApiUrl('MEMBERS');
    return await apiRequest(url, { method: 'GET' });
  },

  // Get Financial Periods
  async getFinancialPeriods(filters = {}) {
    const queryParams = new URLSearchParams();
    Object.keys(filters).forEach(key => {
      if (filters[key] !== undefined && filters[key] !== '') {
        queryParams.append(key, filters[key]);
      }
    });
    const url = queryParams.toString()
      ? `${getApiUrl('FINANCIAL_PERIODS')}?${queryParams.toString()}`
      : getApiUrl('FINANCIAL_PERIODS');
    return await apiRequest(url, { method: 'GET' });
  },

  // Get Single Invoice (alias for getInvoiceById)
  async getInvoice(invoiceId) {
    return await this.getInvoiceById(invoiceId);
  },

  // Invoice Status Management
  async updateInvoiceStatus(invoiceId, status, notes = '') {
    return await apiRequest(getApiUrl('INVOICE_UPDATE_STATUS', invoiceId), {
      method: 'PATCH',
      body: { status, notes }
    });
  },

  async markInvoiceAsPaid(invoiceId, paidDate, paymentMethod = '') {
    return await apiRequest(getApiUrl('INVOICE_MARK_PAID', invoiceId), {
      method: 'PATCH',
      body: { paid_date: paidDate, payment_method: paymentMethod }
    });
  },

  // Invoice Templates
  async getInvoiceTemplates() {
    return await apiRequest(getApiUrl('INVOICE_TEMPLATES'), { method: 'GET' });
  },

  async createInvoiceFromTemplate(templateId, invoiceData) {
    return await apiRequest(getApiUrl('INVOICE_CREATE_FROM_TEMPLATE', templateId), {
      method: 'POST',
      body: invoiceData
    });
  },

  // Payment Tracking
  async getInvoicePayments(invoiceId) {
    return await apiRequest(getApiUrl('INVOICE_PAYMENTS', invoiceId), { method: 'GET' });
  },

  async addInvoicePayment(invoiceId, paymentData) {
    return await apiRequest(getApiUrl('INVOICE_ADD_PAYMENT', invoiceId), {
      method: 'POST',
      body: paymentData
    });
  },

  // Reminder Management
  async sendInvoiceReminder(invoiceId, reminderType = 'overdue') {
    return await apiRequest(getApiUrl('INVOICE_SEND_REMINDER', invoiceId), {
      method: 'POST',
      body: { reminder_type: reminderType }
    });
  },

  async bulkSendReminders(invoiceIds, reminderType = 'overdue') {
    return await apiRequest(getApiUrl('INVOICE_BULK_SEND_REMINDERS'), {
      method: 'POST',
      body: { invoice_ids: invoiceIds, reminder_type: reminderType }
    });
  },

  // Additional bulk operations
  async bulkMarkPaid(invoiceIds, paidDate = null, paymentMethod = '') {
    return await apiRequest(getApiUrl('INVOICE_BULK_MARK_PAID'), {
      method: 'POST',
      body: {
        invoice_ids: invoiceIds,
        paid_date: paidDate || new Date().toISOString().split('T')[0],
        payment_method: paymentMethod
      }
    });
  },

  async bulkCancelInvoices(invoiceIds, reason) {
    return await apiRequest(getApiUrl('INVOICE_BULK_CANCEL'), {
      method: 'POST',
      body: { invoice_ids: invoiceIds, reason: reason }
    });
  }
};

export { invoiceService };
export default invoiceService;